import { useRouter } from 'next/router';

import { useRentalManagerUserPropertyFeedOverrideWebApiHandler_setFeedListingAmenity } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { FEED_OVERRIDES_TAG } from '../constants/FeedOverrideConstants';

import { setAmenityField } from './queries/listingDetailsCache';
import { useGetListingDetails } from './queries/properties';
import { usePropertyEditMutation } from './usePropertyEditMutation';

type UseFeedAmenityMutationOptions = {
  successToastMessage?: string;
};

/**
 * Feed listings can't use the manual per-field amenity endpoints; when the listing is a feed,
 * boolean amenity toggles go through the generic feed-override endpoint (which writes/removes
 * listing tag supplements) instead. `fieldName` is the backend field name, e.g. 'hasBasketballSportCourt'.
 */
export const useFeedAmenityMutation = (options?: UseFeedAmenityMutationOptions) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { mutateAsync } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFeedOverrideWebApiHandler_setFeedListingAmenity,
    skipToast: !options?.successToastMessage,
    successToastMessage: options?.successToastMessage,
    applyToCache: ({ postData }) => [setAmenityField(postData.fieldName, postData.enabled)],
    meta: {
      invalidates: [FEED_OVERRIDES_TAG],
      context: { api: 'setFeedListingAmenity', details: { listingAlias } },
    },
  });

  const isFeedListing = listingDetails?.isFeed ?? false;

  const setFeedAmenity = (fieldName: string, enabled: boolean) =>
    mutateAsync({ postData: { listingAlias, fieldName, enabled } });

  return { isFeedListing, setFeedAmenity };
};
